import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import {
  formatDollarAmount,
  isUserAdminOrManager,
} from '../utils/global/helper';
import PageLayout from '../components/global/PageLayout';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import Error from '../components/global/Error';
import ReuseableTable from '../components/table/ReuseableTable';
import useUser from '../hooks/global/useUser';
import { canIndependentManageShop } from '../helpers/businesses';
import useGetItems from '../hooks/items/useGetItems';
import FullPageModal from '../components/modal/FullPageModal';
import ItemForm from '../components/items/ItemForm';
import { getTextForCategory, updateItemInList } from '../helpers/items';
import { ItemWithCommissions } from '../types/items';
import { trimString } from '../helpers/global';
import { Optional } from '../types/global';
import useUpdateItem from '../hooks/items/useUpdateItem';
import useDeleteItem from '../hooks/items/useDeleteItem';
import useConfirm from '../hooks/useConfirm';
import ItemSideBarModal from '../components/items/ItemSideBarModal';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import CenteredContentLayout from '../components/global/CenteredContentLayout';
import OutlinedPageBox from '../components/global/OutlinedPageBox';
import TableDropdownMenu from '../components/global/TableDropdownMenu';
import PageLoading from '../components/global/PageLoading';

export default function ItemsPage() {
  const confirm = useConfirm();
  const { user } = useUser();
  const hasItemLibrary = useFeatureGuard(Feature.ITEM_LIBRARY);
  const canManageShop =
    !!user && (isUserAdminOrManager(user) || canIndependentManageShop(user));

  const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState<Optional<
    ItemWithCommissions,
    'id'
  > | null>(null);
  const [selectedViewItemId, setSelectedViewItemId] = useState<string | null>(
    null,
  );

  const {
    queryKey,
    data: items = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetItems();
  const { mutate: updateItem, isPending: isUpdating } = useUpdateItem({
    onSuccess: updatedItem => {
      updateItemInList({
        queryKey,
        itemId: updatedItem.id,
        data: updatedItem,
      });
    },
    onError: err => {
      toast.error(err);
    },
  });
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteItem({
    onSuccess: () => {
      refetch();
    },
    onError: err => {
      toast.error(err);
    },
  });

  const selectedViewItem = selectedViewItemId
    ? items.find(i => i.id === selectedViewItemId)
    : null;
  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;

  const onNewItem = () => {
    setIsNewItemModalOpen(true);
  };

  const onItemClick = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setSelectedViewItemId(itemId);
  };

  const error = isError ? 'Error loading items' : '';

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          paddingX: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        topSection={<OnboardingBanner step="ITEM_LIBRARY" />}
        title="Item Library"
        isLoading={isFetching}
        guard={{
          isAllowed: hasItemLibrary && canManageShop,
        }}>
        {!isLoading && (
          <>
            {!!items.length && (
              <>
                <div className="header-right">
                  <Button
                    variant="contained"
                    className="primary"
                    onClick={onNewItem}>
                    New Item
                  </Button>
                </div>
                <ReuseableTable
                  name="Items"
                  handleRowClick={onItemClick}
                  rows={items.map(item => ({
                    rowId: item.id,
                    cells: [
                      item.name,
                      getTextForCategory(item.category),
                      item.isTaxable
                        ? `${productTaxRate * 100}%`
                        : 'Non-taxable item',
                      formatDollarAmount(item.price),
                      <TableDropdownMenu
                        disabled={isUpdating || isFetching || isDeleting}
                        options={[
                          {
                            id: 'edit',
                            label: 'Edit',
                            action: () => {
                              setSelectedEditItem(item);
                            },
                          },
                          {
                            id: 'duplicate',
                            label: 'Duplicate',
                            action: () => {
                              setSelectedEditItem({
                                ...item,
                                id: undefined,
                              });
                            },
                          },
                          {
                            id: 'taxable',
                            label: item.isTaxable
                              ? 'Make non-taxable'
                              : 'Make taxable',
                            action: async () => {
                              updateItem({
                                itemId: item.id,
                                payload: {
                                  isTaxable: !item.isTaxable,
                                },
                              });
                            },
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            action: async () => {
                              const confirmed = await confirm({
                                title: 'Delete Item',
                                message: `Are you sure you want to delete ${item.name}?`,
                                negativeText: 'Cancel',
                                positiveText: 'Delete',
                              });
                              if (!confirmed) return;
                              deleteItem({ itemId: item.id });
                            },
                          },
                        ]}
                      />,
                    ],
                  }))}
                  headerCells={[
                    {
                      id: 'name',
                      label: 'Name',
                    },
                    {
                      id: 'category',
                      label: 'Category',
                    },
                    {
                      id: 'isTaxable',
                      label: 'Sales Tax',
                    },
                    {
                      id: 'price',
                      label: 'Default Price',
                    },
                    {
                      id: 'action',
                      label: '',
                    },
                  ]}
                  emptyMessage=""
                  loading={false}
                />
              </>
            )}

            {!items.length && (
              <OutlinedPageBox>
                <CenteredContentLayout
                  image={{
                    src: '/price-tag.svg',
                    alt: 'Add an item',
                  }}
                  title="Add an item to start selling products"
                  description="Add products to your item library to help speed up checkout and save your products pricing information."
                  button={{
                    text: 'Create an Item',
                    onClick: onNewItem,
                  }}
                  maxWidth="750px"
                  imageMaxWidth="120px"
                />
              </OutlinedPageBox>
            )}
          </>
        )}

        {isLoading && <PageLoading />}
      </PageLayout>

      <FullPageModal
        modalOpen={isNewItemModalOpen || !!selectedEditItem}
        name={
          isNewItemModalOpen || !!selectedEditItem
            ? selectedEditItem?.id
              ? trimString(selectedEditItem.name, 20)
              : 'Create an Item'
            : ''
        }
        setModalOpen={() => {
          setIsNewItemModalOpen(false);
          setSelectedEditItem(null);
        }}>
        {(isNewItemModalOpen || !!selectedEditItem) && (
          <ItemForm
            item={selectedEditItem}
            onSuccess={() => {
              setIsNewItemModalOpen(false);
              setSelectedEditItem(null);
            }}
          />
        )}
      </FullPageModal>
      <ItemSideBarModal
        isOpen={!!selectedViewItem}
        item={selectedViewItem}
        onClose={() => setSelectedViewItemId(null)}
      />
    </>
  );
}
